import { useMemo, useState } from "react";
import Reveal from "../lib/Reveal";

/* Ícones */
function IconPlus({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function IconMinus({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14" />
    </svg>
  );
}

type QA = { q: string; a: string };

const faqs: QA[] = [
  { q: "Implantação tem custo?", a: "Não. O condomínio não paga pela implantação da rede interna. Cada morador só começa a pagar quando ativar seu plano." },
  { q: "Qual a velocidade dos planos?", a: "Planos sob medida com opções simétricas. Áreas comuns e portaria podem ter priorização dedicada." },
  { q: "Tem suporte 24/7?", a: "Sim. Monitoramento pró-ativo e atendimento priorizado para emergências, com SLA." },
  { q: "Como é a ativação no apartamento?", a: "Agendada por unidade e sem obras: instalamos e deixamos tudo testado no mesmo dia." },
  { q: "Existe fidelidade?", a: "Não exigimos fidelidade. Cancelamento a qualquer momento, sem multas." },
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    );
  }, [query]);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
    []
  );

  return (
    <section id="faq" className="relative overflow-hidden py-16 md:py-20">
      {/* BG igual ao About */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -left-24 top-0 h-[34rem] w-[34rem] rounded-full blur-3xl opacity-[.22]"
          style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(255,0,100,.55) 0%, rgba(255,0,100,0) 60%)" }}
        />
        <div
          className="absolute right-[-8rem] bottom-[-10rem] h-[42rem] w-[42rem] rounded-full blur-3xl opacity-[.18]"
          style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(255,77,145,.45) 0%, rgba(255,77,145,0) 60%)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" />
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-extrabold">Perguntas frequentes</h2>
          <p className="text-white/70 mt-2">
            Tire suas dúvidas sobre implantação, ativação e suporte — sem burocracia.
          </p>
        </div>

        <Reveal>
          {/* Busca local (sem ícone) */}
          <div className="mt-6 max-w-xl mx-auto">
            <label className="block">
              <span className="sr-only">Buscar no FAQ</span>
              <input
                type="text"
                placeholder="Buscar no FAQ..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 focus:ring-2 focus:ring-white/30 outline-none py-2.5 px-3 text-sm"
              />
            </label>
          </div>

          {/* Acordeão */}
          <div className="mt-8 grid md:grid-cols-2 gap-4 md:gap-6">
            {filtered.map((f, i) => {
              const id = `faq-${i}`;
              const isOpen = open === i;
              return (
                <article
                  key={i}
                  className="rounded-2xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 hover:ring-white/20 transition"
                >
                  <button
                    id={id}
                    className="w-full flex items-start gap-3 text-left p-5"
                    aria-controls={`${id}-panel`}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : i)}
                  >
                    <span
                      className={
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ring-white/15 " +
                        (isOpen ? "bg-white/15" : "bg-white/10")
                      }
                      aria-hidden
                    >
                      {isOpen ? <IconMinus /> : <IconPlus />}
                    </span>
                    <span className="font-semibold leading-snug">{f.q}</span>
                  </button>

                  <div
                    id={`${id}-panel`}
                    role="region"
                    aria-labelledby={id}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-sm text-white/75 leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="mt-8 text-center text-sm text-white/60">
              Não encontramos resultados. Tente outra palavra-chave ou{" "}
              <a href="#cta" className="underline hover:no-underline">fale com a gente</a>.
            </p>
          )}
        </Reveal>
      </div>

      {/* JSON-LD SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </section>
  );
}
